import { computed, action, observable } from 'mobx';
import { emit, events, on } from '@estee/elc-service-bus';
import { Shade } from '../domain/entities/Shade';
import { IProductShadesSidebarProps } from '../views/product-shades-sidebar/ProductShadesSidebar';
import { ProductBaseController } from './ProductBaseController';
import { ProductRepository } from '../repositories/ProductRepository';
import { diContainer } from '../../service-setup/diContainer';
import serviceNames from '~setup/ProductService';
import { Sku } from '~domain/entities/Sku';

export interface IShadesSidebarProduct {
    productId: string;
}

export interface IShadesByColorFamily {
    [key: string]: Shade[];
}

export class ProductShadesSidebarViewController extends ProductBaseController {
    @observable protected productRepository: ProductRepository;
    @observable private isProductShadesViewOpen: boolean = false;
    @observable public defaultShade: Shade;
    @observable public isLoaded: boolean = false;
    @observable public isShadeSidebarImageEnabled: boolean = true;
    @observable public sidebarButtonRef: React.RefObject<HTMLButtonElement>;

    constructor() {
        super();

        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @action
    public initialize = async (props: IProductShadesSidebarProps) => {
        const {
            config: { selectedSku }
        } = props;

        this.isLoading = false;

        this.updateProduct(selectedSku);

        this.isLoaded = true;
    };

    public updateProduct = (selectedSku: Sku) => {
        const { parentProduct: product, skuId } = selectedSku;

        if (!!product) {
            this.setProduct(product);
            this.setSelectedSkuId(skuId);
        }
    };

    @computed
    get data() {
        return {
            ...this.baseData,
            isProductShadesViewOpen: this.isProductShadesViewOpen,
            shades: this.shades,
            isLoaded: this.isLoaded,
            isLoading: this.isLoading,
            shadesByColorFamily: this.shadesByColorFamily,
            selectedSku: this.selectedSku,
            largeImages: this.largeImages,
            imageSliderSettings: this.imageSliderSettings,
            isShadeSidebarImageEnabled: this.isShadeSidebarImageEnabled,
            sidebarButtonRef: this.sidebarButtonRef
        };
    }

    @action
    public onCloseProductShadesView = () => {
        this.isProductShadesViewOpen = false;
        emit(events.SHADE_SIDEBAR_CLOSED);
    };

    @action
    public async onProductShadesViewClick(
        defaultShade?: Shade,
        sidebarButtonRef?: React.RefObject<HTMLButtonElement>
    ) {
        this.isProductShadesViewOpen = true;
        if (defaultShade) {
            this.setDefaultShade(defaultShade);
        }
        if (sidebarButtonRef) {
            this.sidebarButtonRef = sidebarButtonRef;
        }
    }

    @action
    private setDefaultShade(defaultShade: Shade) {
        this.defaultShade = defaultShade;
    }

    @computed
    private get shades(): Shade[] {
        return this.product ? this.product.shades : [];
    }

    @computed
    get shadesByColorFamily() {
        if (this.product) {
            return this.product.skus.reduce((shadesByColorFamily: IShadesByColorFamily, sku) => {
                const { colorFamily, shades } = sku;
                const value = colorFamily.length > 0 ? colorFamily[0].value : '';
                shadesByColorFamily[value] = (shadesByColorFamily[value] || []).concat(
                    Array.from(shades)
                );

                return shadesByColorFamily;
            }, {});
        }
    }

    @computed
    private get imageSliderSettings() {
        return this.imageCarouselSettings && this.imageCarouselSettings.productShadesSidebar;
    }

    @action
    public setIsShadeSidebarImageEnabled = (isEnabled: boolean) => {
        this.isShadeSidebarImageEnabled = isEnabled;
    };

    @on(events.SHADE_SIDEBAR_IMAGE_ENABLED)
    public enableShadeSidebarImage = (payload: { isEnabled: boolean }) => {
        this.setIsShadeSidebarImageEnabled(payload.isEnabled);
    };

    @on(events.SHADE_SIDEBAR_OPENED)
    public updateShadeSidebarOpenState = () => {
        this.onProductShadesViewClick();
    };
}
